import {
  handFor, scaleFor, deadScaleFor,
  TOKEN_H, CORPSE_REL,
} from '../spriteLayout';
import type { Fighter, Scene } from './types';

// --- sprite anchoring helpers ---
// `f.skin` is the skin's file prefix (e.g. `t1`); body keys are `${prefix}_${part}`.
export const spriteOf = (sc: Scene, f: Fighter) =>
  sc.bodies[`${f.skin}_${f.stance}`]
  ?? sc.bodies[`${f.skin}_rifle`]
  ?? Object.values(sc.bodies)[0];
export const corpseOf = (sc: Scene, f: Fighter) =>
  sc.bodies[`${f.skin}_dead`] ?? spriteOf(sc, f);
export const weaponOf = (sc: Scene, f: Fighter) =>
  (f.weaponSkin ? sc.weaponSkins[f.weaponSkin] : undefined) ?? sc.weapons[f.weaponKey] ?? Object.values(sc.weapons)[0];

export const scaleOf = (sc: Scene, f: Fighter) =>
  (TOKEN_H * scaleFor(f.skin)) / spriteOf(sc, f).bh;
// Corpse is normalized by ITS OWN bounding box (long axis ≈ standing height), NOT
// by the live-body scale — otherwise each skin's dead/chibi pixel-ratio leaks in
// and corpses render at wildly different sizes. CORPSE_REL tunes lying length vs
// standing height. Per-skin `scaleFor` still applies for consistency with the live size.
export const corpseScaleOf = (sc: Scene, f: Fighter) => {
  const cp = corpseOf(sc, f);
  return (TOKEN_H * CORPSE_REL * scaleFor(f.skin) * deadScaleFor(f.skin)) / Math.max(cp.bw, cp.bh);
};
export const handLocal = (sc: Scene, f: Fighter) => {
  const sp = spriteOf(sc, f);
  const s = scaleOf(sc, f);
  const hand = handFor(f.skin, f.stance); // per-skin override (skinAnchors.json), else default
  const dx = (sp.bx + sp.bw * hand.x - (sp.bx + sp.bw / 2)) * s * f.dir;
  const dy = (sp.by + sp.bh * hand.y - (sp.by + sp.bh)) * s;
  return { dx, dy, s };
};
export const handOf = (sc: Scene, f: Fighter) => {
  const { dx, dy, s } = handLocal(sc, f);
  return { x: f.x + dx, y: f.y + dy, s };
};
// muzzleOf moved to fighters.ts — it must be computed in the SPLIT geometry that actually
// draws the weapon (whole-body anchors here don't match the drawn barrel).
